/**
 * ProShip Direct v2 — client-side store.
 *
 * Every capability this module needs is unbuilt in proship_mongo: the per-merchant plan flag
 * (TRD 9.3 prozo_direct_enabled), own courier accounts, per-account config, disabling a Prozo
 * courier, and merchant-editable allocation rules. So state lives in localStorage until those
 * endpoints exist, and swapping to the real thing means replacing this file's read/write with
 * RTK Query calls — the hooks in ./hooks.js are the only consumers.
 *
 * DEVIATION: not Redux. The panel's store is persisted wholesale to localStorage, and
 * UI_CODING_STANDARDS 2 warns against putting bulk state there; this is also throwaway state
 * that must not survive as a slice once the API lands. One module-scoped subscription keeps
 * every open page in sync, which is what a slice would otherwise be buying us.
 */
import { useCallback, useEffect, useState } from 'react';
import { DEFAULT_STATE } from './defaults';

/**
 * BUMP THIS whenever a slice changes shape.
 *
 * readAll merges the persisted blob OVER the defaults, which is right for added fields and wrong
 * for changed ones: a v1 `returnsQc` of {qcAtDoorstep, checks, refundTrigger} overrode the v2
 * default and left the QC screen reading `setup.selected.includes` on an object that has no
 * `selected`. The page threw and rendered nothing.
 *
 * A version in the key is the cheap fix — an old blob is simply not found, so the module starts
 * from DEFAULT_STATE. Local edits from the previous version are lost, which for a store that only
 * exists until the endpoints land is the right trade.
 *
 *   v1 → v2: returnsQc took the grounded rvpQc shape; pace moved from {weights} to
 *            {profile, overrides, history}; courierPriority, trackingPage, rateCards,
 *            serviceabilityLanes and zoneMaps were added.
 */
const KEY = 'proshipDirectPlan.v2';

const listeners = new Set();
let cache = null;

function readAll() {
  if (cache) return cache;
  try {
    const raw = window.localStorage.getItem(KEY);
    cache = raw ? { ...DEFAULT_STATE, ...JSON.parse(raw) } : { ...DEFAULT_STATE };
    // Older keys are dead weight once the version moves on.
    window.localStorage.removeItem('proshipDirectPlan.v1');
  } catch {
    // Corrupt or unavailable storage (private mode, quota) must not blank the screen.
    cache = { ...DEFAULT_STATE };
  }
  return cache;
}

function writeAll(next) {
  cache = next;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // Keep the in-memory value so the UI stays consistent for this session.
  }
  listeners.forEach((fn) => fn());
}

/** Replace one top-level slice. `value` may be a value or an updater function. */
export function setSlice(slice, value) {
  const current = readAll();
  const nextValue = typeof value === 'function' ? value(current[slice]) : value;
  writeAll({ ...current, [slice]: nextValue });
}

export function getSlice(slice) {
  return readAll()[slice];
}

/** Reset everything — used by the "reset sample data" control on the module home page. */
export function resetStore() {
  writeAll({ ...DEFAULT_STATE });
}

/**
 * Subscribe to one top-level slice. Returns [value, setValue]; setValue accepts a value or an
 * updater, matching useState so call sites read the same way as the rest of the panel.
 */
export function useSlice(slice) {
  const [value, setValue] = useState(() => readAll()[slice]);

  useEffect(() => {
    const sync = () => setValue(readAll()[slice]);
    listeners.add(sync);
    sync();
    return () => listeners.delete(sync);
  }, [slice]);

  const update = useCallback((next) => setSlice(slice, next), [slice]);

  return [value, update];
}
